import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControlLabel from "@mui/material/FormControlLabel"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Link from "@mui/material/Link"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Link as RouterLink } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { formatCurrencyLong } from "../../../common/utils"
import { useLoadAvailableMLS } from "../../../smartsetter-utils/filters-dialog/filter-bodies/MLSIDFilterBody"
import DialogHeader from "../components/DialogHeader"
import { selectCRMSettings } from "../reducer"
import { PLAN_BILLING_SECTIONS, createPlanBillingURL } from "../settings"
import MlsPayLaterPanel from "./MlsPayLaterPanel"
import {
  boardsLabel,
  creditsRemainingLabel,
  dealCreditCover,
} from "./mlsDealCredit"
import { MLS_ACTIVATION_TIMELINE } from "./mlsPurchaseActivationCopy"
import {
  MLS_PURCHASE_CODE_OF_ETHICS_BODY,
  MLS_PURCHASE_CODE_OF_ETHICS_TITLE,
} from "./mlsPurchaseCodeOfEthics"
import { useMlsAddonOffer } from "./useMlsAddonOffer"

function createEmptyMlsLine() {
  return {
    key: `${Date.now()}-${Math.random()}`,
    mls: null,
    input: "",
    mls_login_id: "",
    mls_web_address: "",
    username: "",
    password: "",
    showPassword: false,
  }
}

function lineCredentialsComplete(line) {
  return (
    line.mls_login_id.trim() &&
    line.mls_web_address.trim() &&
    line.username.trim() &&
    line.password
  )
}

function lineComplete(line) {
  return Boolean(line.mls) && lineCredentialsComplete(line)
}

/**
 * client + standalone: the /buy-mls page, where an MLS purchase link (not a
 * login) authenticates the requests and there is no app to link back into.
 */
export default function BuyMoreMlsDialog({
  open,
  onClose,
  client = null,
  standalone = false,
}) {
  const appAxios = useAuthorizedAxiosClient()
  const axios = client || appAxios
  const crmSettings = useSelector(selectCRMSettings)

  const {
    offer,
    loading: offerLoading,
    purchaseEnabled,
    checkoutReady,
    canCheckout,
    checkoutBlockedReason,
  } = useMlsAddonOffer(client)

  const ownedMlsIds = useMemo(
    () =>
      new Set(
        standalone
          ? offer?.owned_mls_ids || []
          : (crmSettings?.settings?.mlss || []).map((m) => m.id)
      ),
    [crmSettings, offer, standalone]
  )
  const { availableMLS, availableMlsLoaded } = useLoadAvailableMLS()
  const selectableMLS = useMemo(
    () => availableMLS.filter((mls) => !ownedMlsIds.has(mls.id)),
    [availableMLS, ownedMlsIds]
  )

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [officeName, setOfficeName] = useState("")
  const [officeAddress, setOfficeAddress] = useState("")
  const [city, setCity] = useState("")
  const [stateCode, setStateCode] = useState("")
  const [mlsLines, setMlsLines] = useState([createEmptyMlsLine()])
  const [ethicsAccepted, setEthicsAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  // Pay later: the order is saved and someone else pays via a payment link.
  const [payLaterOpen, setPayLaterOpen] = useState(false)
  const [payerEmail, setPayerEmail] = useState("")
  const [payLaterResult, setPayLaterResult] = useState(null)

  const completedLines = mlsLines.filter(lineComplete)
  const lineCount = completedLines.length
  const unitPrice = offer?.unit_price ?? 0
  // Boards already paid through the client's plan (sold on a CRM deal).
  const cover = dealCreditCover(offer, lineCount)
  const totalMonthly = unitPrice * cover.charged

  const selectedMlsIds = new Set(mlsLines.map((l) => l.mls?.id).filter(Boolean))

  const loginIds = completedLines.map((line) =>
    line.mls_login_id.trim().toLowerCase()
  )
  const uniqueLoginIds = new Set(loginIds)

  function handleAddLine() {
    setMlsLines((prev) => [...prev, createEmptyMlsLine()])
  }

  function handleRemoveLine(key) {
    setMlsLines((prev) =>
      prev.length <= 1 ? prev : prev.filter((line) => line.key !== key)
    )
  }

  function updateLine(key, patch) {
    setMlsLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...patch } : line))
    )
  }

  function formValid() {
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !officeName.trim() ||
      !officeAddress.trim() ||
      !city.trim() ||
      !stateCode.trim()
    ) {
      return false
    }
    if (lineCount === 0) {
      return false
    }
    if (selectedMlsIds.size !== mlsLines.filter((l) => l.mls).length) {
      return false
    }
    if (mlsLines.some((line) => !lineComplete(line))) {
      return false
    }
    if (uniqueLoginIds.size !== loginIds.length) {
      return false
    }
    if (!ethicsAccepted) {
      return false
    }
    // A fully covered order never reaches Zoho, so it does not need pricing.
    const paymentReady = cover.fullyCovered ? purchaseEnabled : checkoutReady
    return paymentReady && canCheckout && !checkoutBlockedReason
  }

  function buildLinesPayload() {
    return completedLines.map((line) => ({
      mls_id: line.mls.id ?? line.mls.pk,
      mls_login_id: line.mls_login_id.trim(),
      mls_web_address: line.mls_web_address.trim(),
      username: line.username.trim(),
      password: line.password,
    }))
  }

  function handleSubmit({ payLater = false } = {}) {
    setSubmitting(true)
    setSubmitError("")
    axios
      .post("/billing/api/mls-purchases/", {
        contact_details: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          office_name: officeName.trim(),
          office_address: officeAddress.trim(),
          city: city.trim(),
          state_code: stateCode.trim(),
        },
        lines: buildLinesPayload(),
        code_of_ethics_accepted: ethicsAccepted,
        ...(payLater
          ? { pay_later: true, payer_email: payerEmail.trim() }
          : {}),
      })
      .then(({ data }) => {
        if (payLater && !data.covered_by_plan) {
          setPayLaterResult(data)
          return
        }
        // Fully covered by the plan: no checkout, straight to the result page.
        window.location.href = data.redirect_url || data.checkout_url
      })
      .catch((err) => {
        const data = err.response?.data
        const detail = data?.detail || data?.payer_email?.[0]
        setSubmitError(
          typeof detail === "string"
            ? detail
            : payLater
            ? "Could not save the order. Please try again."
            : "Could not start checkout. Please try again."
        )
      })
      .finally(() => setSubmitting(false))
  }

  function handleClose(...args) {
    // The page version needs to know an order was saved, not abandoned.
    const saved = Boolean(payLaterResult)
    setPayLaterOpen(false)
    setPayLaterResult(null)
    setPayerEmail("")
    onClose(saved ? { savedForPayer: true } : args[0])
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      // The standalone page keeps its account header bar visible above it.
      PaperProps={standalone ? { sx: { mt: 10 } } : undefined}
    >
      <DialogHeader title="Purchase MLS" onClose={handleClose} />
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Select an MLS and share your login credentials for it so we can set
            up your access.{" "}
            {standalone ? null : (
              <>
                View past orders in{" "}
                <Link
                  component={RouterLink}
                  to={createPlanBillingURL(PLAN_BILLING_SECTIONS.mlsPurchases)}
                  onClick={onClose}
                >
                  Settings → MLS purchase history
                </Link>
                .{" "}
              </>
            )}
            {MLS_ACTIVATION_TIMELINE}
          </Typography>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Your details
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid xs={12} sm={6}>
                <TextField
                  label="First name"
                  required
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Grid>
              <Grid xs={12} sm={6}>
                <TextField
                  label="Last name"
                  required
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Grid>
              <Grid xs={12}>
                <TextField
                  label="Office name"
                  required
                  fullWidth
                  value={officeName}
                  onChange={(e) => setOfficeName(e.target.value)}
                />
              </Grid>
              <Grid xs={12}>
                <TextField
                  label="Office address"
                  required
                  fullWidth
                  value={officeAddress}
                  onChange={(e) => setOfficeAddress(e.target.value)}
                />
              </Grid>
              <Grid xs={12} sm={8}>
                <TextField
                  label="City"
                  required
                  fullWidth
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </Grid>
              <Grid xs={12} sm={4}>
                <TextField
                  label="State code"
                  required
                  fullWidth
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Row
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="overline" color="text.secondary">
                MLS access
              </Typography>
              <Chip
                size="small"
                label={`${lineCount} added`}
                variant="outlined"
                color={lineCount ? "primary" : "default"}
              />
            </Row>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Select an MLS from the list, then provide your own login
              credentials for it. Credentials are required so access can be set
              up on your behalf.
            </Typography>
            <Column sx={{ gap: 2, mt: 1.5 }}>
              {mlsLines.map((line, index) => (
                <Box
                  key={line.key}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Row
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      MLS #{index + 1}
                    </Typography>
                    {mlsLines.length > 1 ? (
                      <IconButton
                        size="small"
                        aria-label="Remove MLS row"
                        onClick={() => handleRemoveLine(line.key)}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Row>
                  <Grid container spacing={2}>
                    <Grid xs={12}>
                      <Autocomplete
                        options={selectableMLS.filter(
                          (mls) =>
                            mls.id === line.mls?.id ||
                            !mlsLines.some(
                              (other) =>
                                other.key !== line.key &&
                                other.mls?.id === mls.id
                            )
                        )}
                        loading={!availableMlsLoaded}
                        value={line.mls}
                        inputValue={line.input}
                        onInputChange={(e, value) =>
                          updateLine(line.key, { input: value })
                        }
                        onChange={(e, value) =>
                          updateLine(line.key, { mls: value })
                        }
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="MLS"
                            required
                            placeholder="Search MLS..."
                          />
                        )}
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        label="MLS login ID"
                        required
                        fullWidth
                        value={line.mls_login_id}
                        onChange={(e) =>
                          updateLine(line.key, {
                            mls_login_id: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        label="MLS web address"
                        required
                        fullWidth
                        value={line.mls_web_address}
                        onChange={(e) =>
                          updateLine(line.key, {
                            mls_web_address: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        label="Username"
                        required
                        fullWidth
                        value={line.username}
                        onChange={(e) =>
                          updateLine(line.key, { username: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        label="Password"
                        required
                        fullWidth
                        type={line.showPassword ? "text" : "password"}
                        value={line.password}
                        onChange={(e) =>
                          updateLine(line.key, { password: e.target.value })
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography
                                component="button"
                                type="button"
                                variant="caption"
                                sx={{
                                  border: 0,
                                  bgcolor: "transparent",
                                  color: "primary.main",
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                                onClick={() =>
                                  updateLine(line.key, {
                                    showPassword: !line.showPassword,
                                  })
                                }
                              >
                                {line.showPassword ? "HIDE" : "SHOW"}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
              <LoadingButton
                startIcon={<AddOutlinedIcon />}
                onClick={handleAddLine}
                variant="outlined"
                sx={{ alignSelf: "flex-start" }}
              >
                Add another MLS
              </LoadingButton>
            </Column>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">
              Code of ethics
            </Typography>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              Required for every MLS purchase.
            </Typography>
            <Box
              sx={{
                mt: 1,
                p: 2,
                maxHeight: 200,
                overflow: "auto",
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.grey[50],
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {MLS_PURCHASE_CODE_OF_ETHICS_TITLE}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-line" }}
              >
                {MLS_PURCHASE_CODE_OF_ETHICS_BODY}
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={ethicsAccepted}
                  onChange={(e) => setEthicsAccepted(e.target.checked)}
                />
              }
              label="I have read and understand this Code of Ethics. *"
            />
          </Box>

          {lineCount > 0 && (cover.covered > 0 || offer?.unit_price != null) ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.grey[50],
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Order summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {lineCount} MLS
              </Typography>
              {cover.covered > 0 ? (
                <Typography
                  variant="body2"
                  sx={{ mt: 1 }}
                  data-testid="mls-deal-credit-cover"
                >
                  <strong>
                    {boardsLabel(cover.covered)} covered by your plan
                  </strong>{" "}
                  — no charge ({creditsRemainingLabel(cover)}).
                </Typography>
              ) : null}
              {cover.charged > 0 && offer?.unit_price != null ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>
                    {cover.covered > 0
                      ? `${boardsLabel(cover.charged)} ${formatCurrencyLong(
                          unitPrice
                        )}/mo:`
                      : "Recurring charge:"}
                  </strong>{" "}
                  {formatCurrencyLong(unitPrice)}
                  /month × {cover.charged} MLS ={" "}
                  {formatCurrencyLong(totalMonthly)}
                  /month (confirmed on Zoho checkout). Turn off renewal at the
                  end of your paid term from MLS purchase history in Settings
                  {standalone ? " once you log in to OneBase" : ""}.
                </Typography>
              ) : null}
              {cover.covered > 0 && cover.charged > 0 && cover.remaining > 0 ? (
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Your plan credit is used only once the payment for the other{" "}
                  {boardsLabel(cover.charged)} goes through.
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {payLaterOpen || payLaterResult ? (
            <MlsPayLaterPanel
              payerEmail={payerEmail}
              onPayerEmailChange={setPayerEmail}
              result={payLaterResult}
            />
          ) : null}
          {submitError ? (
            <Typography color="error.main">{submitError}</Typography>
          ) : null}
          {checkoutBlockedReason && !offerLoading ? (
            <Typography color="text.secondary">
              {checkoutBlockedReason}
            </Typography>
          ) : null}
          {purchaseEnabled &&
          !canCheckout &&
          !checkoutBlockedReason &&
          !offerLoading ? (
            <Typography color="text.secondary">
              Only the account owner can complete MLS purchases. Ask your
              account owner to use Purchase MLS or contact support.
            </Typography>
          ) : null}
          {purchaseEnabled &&
          canCheckout &&
          !checkoutReady &&
          !cover.fullyCovered &&
          !offerLoading ? (
            <Typography color="text.secondary">
              {offer?.message ||
                "MLS pricing is temporarily unavailable. Please try again or contact support."}
            </Typography>
          ) : null}
          {!purchaseEnabled && !offerLoading ? (
            <Typography color="text.secondary">
              MLS purchase is not enabled on this environment.
            </Typography>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions>
        {payLaterResult ? (
          <Button variant="contained" onClick={() => handleClose()}>
            Done
          </Button>
        ) : payLaterOpen ? (
          <>
            <Button onClick={() => setPayLaterOpen(false)}>Back</Button>
            <LoadingButton
              variant="contained"
              loading={submitting || offerLoading}
              disabled={!formValid()}
              onClick={() => handleSubmit({ payLater: true })}
            >
              Create payment link
            </LoadingButton>
          </>
        ) : (
          <>
            {cover.fullyCovered ? null : (
              <Button
                disabled={!formValid() || submitting}
                onClick={() => setPayLaterOpen(true)}
              >
                Send to someone else to pay
              </Button>
            )}
            <LoadingButton
              variant="contained"
              loading={submitting || offerLoading}
              disabled={!formValid() || submitting}
              onClick={() => handleSubmit()}
            >
              {cover.fullyCovered ? "Set up (covered by your plan)" : "Pay now"}
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
